<template>
	<template v-if="!status.overallStatus || status.overallStatus === 'pending'">
		<template v-if="!dataRequest.dynamicLink">
			<h2>
				Dataverzoek wordt aangemaakt voor
				{{ getCustomerLabel(dataStore.data.customers, customer.id) }}
			</h2>
			<p class="text-sm text-gray-500">
				Het kan even duren voordat het dataverzoek is aangemaakt. Zodra het
				verzoek klaar is kun je de link delen met je klant.
			</p>
		</template>
		<template v-else>
			<div class="flex flex-col gap-4">
				<div class="flex gap-2 items-center">
					<div class="flex flex-col">
						<h2>
							<template v-if="isChild">
								Gegevens van
								{{ getCustomerLabel(dataStore.data.customers, customer.id) }}
								ook op HDN klaarzetten voor geldverstrekkers?
							</template>
							<template v-else>
								Deel deze link met
								{{ getCustomerLabel(dataStore.data.customers, customer.id) }}
							</template>
						</h2>
						<template v-if="isChild">
							<div class="py-2">
								<AppButton
									variant="link"
									class="self-start"
									@click="showSupportedLenders = !showSupportedLenders"
								>
									{{
										showSupportedLenders
											? "< Verberg ondersteunde geldverstrekkers"
											: "> Toon ondersteunde geldverstrekkers"
									}}
								</AppButton>
								<ul v-if="showSupportedLenders" class="list-disc list-inside">
									<li
										v-for="lender in supportedLenders"
										:key="lender.receiverId"
										class="text-sm"
									>
										{{
											lender.hdnReceiver?.hdnProvider?.label ??
											splitHdnValue(lender.hdnReceiver?.hdnProvider?.name)[1]
										}}
									</li>
								</ul>
							</div>
							<span class="text-sm text-gray-500">
								Deel deze link dan ook met de klant:
							</span>
						</template>
					</div>
					<div
						v-if="dataRequest.channel && !isChild"
						:title="dataRequestChannelLabels[dataRequest.channel]"
					>
						<component
							:is="requestIcon"
							class="w-4 h-4 text-(--text-secondary)"
						/>
					</div>
				</div>
				<DataRequestShare :dynamic-link="dataRequest.dynamicLink" />
			</div>
		</template>
	</template>
	<template v-else-if="status.overallStatus === 'opened'">
		<h2>
			{{ getCustomerLabel(dataStore.data.customers, customer.id) }} is bezig met
			het dataverzoek
		</h2>
		<p class="text-sm text-gray-500">
			Zodra de gegevens zijn aangeleverd, zijn ze hier te vinden.
		</p>
		<ValidationMessagesList :status="status" />
	</template>
	<template v-else-if="status.overallStatus === 'completed'">
		<div class="flex flex-col gap-2">
			<h2>
				Gegevens aangeleverd{{
					dataRequest.channel && dataRequestChannelLabels[dataRequest.channel]
						? ` via ${dataRequestChannelLabels[dataRequest.channel]}`
						: ""
				}}
			</h2>
			<ValidationMessagesList :status="status" />
			<AppButton
				v-if="!isChild"
				:disabled="!canApplyData"
				variant="primary"
				class="self-stretch"
				@click="applyData"
			>
				{{ dataApplicationStateLabel[dataApplicationState] }}
			</AppButton>

			<div v-if="child?.dynamicLink" class="flex flex-col gap-2 pt-4">
				<hr class="w-full py-4 pb-2" />
				<DataRequestStatusCard
					:data-request="child"
					:customer
					:is-child="true"
				/>
			</div>

			<code v-if="content && showValues" class="text-xs">
				<pre class="w-full">{{ stringifyObject(content) }}</pre>
			</code>
		</div>
	</template>
	<template v-else-if="status.overallStatus === 'failed'">
		<div class="flex flex-col gap-4">
			<h2>
				{{
					hasPendingSources || hasSuccessfulSources
						? "Gegevens konden niet volledig opgehaald worden"
						: "Gegevens konden niet opgehaald worden"
				}}
			</h2>
			<ValidationMessagesList :status="status" />

			<AppButton
				v-if="!isChild"
				:disabled="!canApplyData"
				variant="primary"
				class="self-stretch"
				@click="applyData"
			>
				{{ dataApplicationStateLabel[dataApplicationState] }}
			</AppButton>
		</div>
	</template>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import AppButton from "@/components/AppButton.vue";
import type { InventarisationCustomer } from "@loanlink-nl/model-app";
import { getCustomerLabel } from "@/helpers/inventarisation/customers";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import {
	parseDatakeeperPredicates,
	useDataRequestStatus,
	type DataRequest,
} from "@/composables/useDataRequestStatus";
import type { Row } from "@rocicorp/zero";
import { mutators, type Schema } from "@loanlink-nl/zero";
import type { BrondataMessages } from "@loanlink-nl/model-mapping";
import {
	mergeBrondataWithDossierData,
	mergeDatakeeperWithDossierData,
} from "@loanlink-nl/model-mapping";
import { DateTime } from "luxon";
import { useUserStore } from "@/store/useUserStore";
import HdnIcon from "@/assets/icons/hdn.svg";
import DatakeeperIcon from "@/assets/icons/datakeeper.svg";
import { useQuery, useZero } from "@/composables/useZero";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { stringifyObject } from "@/helpers/uiHelpers";
import { queries } from "@loanlink-nl/zero";

const props = defineProps<{
	dataRequest: Readonly<DataRequest>;
	customer: InventarisationCustomer;
	isChild?: boolean;
	validations?: Readonly<Row<Schema["tables"]["dataRequestValidationTable"]>[]>;
	child?: Readonly<DataRequest>;
}>();

const showSupportedLenders = ref(false);

const { data: supportedLenders } = useQuery(() =>
	queries.receivers.datakeeperSupported(),
);

const dataRequestChannelLabels: Record<
	Exclude<DataRequest["channel"], null>,
	string
> = {
	hdn: "HDN",
	datakeeper: "Datakeeper",
};

const dataStore = useDossierDataStore();
const showValues = inject("DEBUG_SHOW_MODELVALUES", false);

const requestIcon = computed(() => {
	if (props.dataRequest.channel === "datakeeper") {
		return DatakeeperIcon;
	}

	return HdnIcon;
});

function mapContent(content: any) {
	if (!content) {
		return;
	}

	const items = content
		.map((x: any) => ({
			value: x.details.predicateValues,
			context: x.context[0],
		}))
		.toSorted((a: any, b: any) => a.context - b.context);

	return items.reduce((red: any, x: any) => {
		red[x.context.split("#")[1]] ??= {};
		Object.assign(red[x.context.split("#")[1]], x.value);
		return red;
	}, {});
}

const content = computed(() => {
	if (!props.dataRequest.content) {
		return;
	}

	if (props.dataRequest.channel === "datakeeper") {
		return mapContent(props.dataRequest.content);
	}

	return props.dataRequest.content;
});

const hasSuccessfulSources = computed(() => {
	return status.value.sourceStatuses.some((s) => s.status === "success");
});
const hasPendingSources = computed(() => {
	return status.value.sourceStatuses.some((s) => s.status === "pending");
});

type DataApplicationState =
	| "not-applied"
	| "applying"
	| "applied"
	| "applied-retryable";
const dataApplicationStateLabel: Record<DataApplicationState, string> = {
	"not-applied": "Gegevens toepassen",
	applying: "Gegevens toepassen...",
	applied: "Gegevens toegepast",
	"applied-retryable": "Gegevens opnieuw toepassen",
};
const dataApplicationState = ref<DataApplicationState>(
	props.dataRequest.appliedAt ? "applied-retryable" : "not-applied",
);

const canApplyData = computed(() => {
	return (
		dataApplicationState.value !== "applying" &&
		dataApplicationState.value !== "applied" &&
		hasSuccessfulSources
	);
});

const status = useDataRequestStatus(
	() => props.dataRequest,
	() => props.validations,
);

function applyData() {
	const user = useUserStore().user;
	if (!user) {
		throw new Error("User not found");
	}

	dataApplicationState.value = "applying";

	if (props.dataRequest.channel === "hdn") {
		const newData = mergeBrondataWithDossierData(
			dataStore.data,
			props.dataRequest.content as BrondataMessages,
			props.customer.id,
		);

		dataStore.data = newData;
	} else if (props.dataRequest.channel === "datakeeper") {
		const datakeeperData = parseDatakeeperPredicates(props.dataRequest.content);
		if (!datakeeperData) {
			throw new Error("Failed to parse datakeeper data");
		}

		const newData = mergeDatakeeperWithDossierData(
			dataStore.data,
			Object.entries(datakeeperData).reduce((red, [key, value]) => {
				if (value.status === "CONFIRMED") {
					red[key] = value.value;
				}
				return red;
			}, {} as any),
			props.customer.id,
		);

		dataStore.data = newData;
	}

	const z = useZero();
	z.value.mutate(
		mutators.dataRequestTable.applyData({
			id: props.dataRequest.id,
			appliedAt: DateTime.now().toMillis(),
		}),
	);

	dataApplicationState.value = "applied";
	setTimeout(() => {
		dataApplicationState.value = "applied-retryable";
	}, 5000);
}
</script>
